
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useNavigate,
} from 'react-router-dom';
import {
  Eye,
  Loader2,
  Pencil,
  Plus,
  Search,
  Users,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';

import { academicApi } from '../../academics/services/academicApi';

import {
  getApiErrorMessage,
  resolveMediaUrl,
  studentApi,
} from '../services/studentApi';

const unwrapStudentList = (response) => {
  const payload =
    response?.data ?? response;

  if (Array.isArray(payload)) {
    return {
      results: payload,
      count: payload.length,
      totalPages: 1,
    };
  }

  if (
    Array.isArray(payload?.results)
  ) {
    return {
      results: payload.results,
      count:
        payload.count ??
        payload.total ??
        payload.results.length,
      totalPages:
        payload.total_pages ??
        payload.meta?.total_pages ??
        1,
    };
  }

  if (
    Array.isArray(payload?.data)
  ) {
    return {
      results: payload.data,
      count:
        payload.count ??
        payload.total ??
        payload.data.length,
      totalPages:
        payload.total_pages ??
        payload.meta?.total_pages ??
        1,
    };
  }

  return {
    results: [],
    count: 0,
    totalPages: 1,
  };
};

const getInitials = (name) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return 'ST';
  }

  if (parts.length === 1) {
    return parts[0]
      .slice(0, 2)
      .toUpperCase();
  }

  return `${parts[0][0]}${
    parts[parts.length - 1][0]
  }`.toUpperCase();
};

const StudentPhoto = ({
  photo,
  name,
}) => {
  const [failed, setFailed] =
    useState(false);

  const url = resolveMediaUrl(photo);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (url && !failed) {
    return (
      <img
        src={url}
        alt={name}
        className="h-10 w-10 rounded-full object-cover"
        onError={() =>
          setFailed(true)
        }
      />
    );
  }

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">
      {getInitials(name)}
    </div>
  );
};

const StudentListPage = () => {
  const navigate = useNavigate();
  const { schoolId, activeAcademicYear, activeAcademicYearId } = useSchool();
  const academicYear = activeAcademicYearId || '';

  const [students, setStudents] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [query, setQuery] =
    useState('');

  const [
    classLevel,
    setClassLevel,
  ] = useState('');

  const [section, setSection] =
    useState('');

  const [
    statusFilter,
    setStatusFilter,
  ] = useState('');

  const [
    genderFilter,
    setGenderFilter,
  ] = useState('');

  const [page, setPage] =
    useState(1);

  const [pageSize, setPageSize] =
    useState(10);

  const [
    totalCount,
    setTotalCount,
  ] = useState(0);

  const [
    totalPages,
    setTotalPages,
  ] = useState(1);

  const [
    classLevels,
    setClassLevels,
  ] = useState([]);

  const [sections, setSections] =
    useState([]);

  const [
    loadingClasses,
    setLoadingClasses,
  ] = useState(false);

  const [
    loadingSections,
    setLoadingSections,
  ] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadClassesForWorkingYear = async () => {
      setClassLevel('');
      setSection('');
      setSections([]);
      setPage(1);

      if (!schoolId || !activeAcademicYearId) {
        setClassLevels([]);
        return;
      }

      setLoadingClasses(true);
      try {
        const data = await academicApi.getClassLevels(schoolId, {
          academicYearId: activeAcademicYearId,
          status: 'ACTIVE',
        });
        if (!cancelled) setClassLevels(data || []);
      } catch (requestError) {
        if (!cancelled) {
          setError(
            getApiErrorMessage(requestError, 'Unable to load classes.'),
          );
        }
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    };

    loadClassesForWorkingYear();
    return () => {
      cancelled = true;
    };
  }, [schoolId, activeAcademicYearId]);

  const handleClassChange =
    async (event) => {
      const classId =
        event.target.value;

      setClassLevel(classId);
      setSection('');
      setSections([]);
      setPage(1);

      if (!classId) {
        return;
      }

      setLoadingSections(true);

      try {
        const data =
          await academicApi.getSections(
            schoolId,
            {
              academicYearId:
                academicYear,
              classLevelId: classId,
              status: 'ACTIVE',
            },
          );

        setSections(data || []);
      } catch (requestError) {
        setError(
          getApiErrorMessage(
            requestError,
            'Unable to load sections.',
          ),
        );
      } finally {
        setLoadingSections(false);
      }
    };

  useEffect(() => {
    if (!schoolId) {
      return undefined;
    }

    let cancelled = false;

    const timeout = window.setTimeout(
      async () => {
        setLoading(true);
        setError('');

        try {
          const response =
            await studentApi.getStudents(
              schoolId,
              {
                q: query.trim(),
                academicYearId:
                  academicYear,
                classLevelId:
                  classLevel,
                sectionId: section,
                status:
                  statusFilter,
                gender:
                  genderFilter,
                page,
                pageSize,
              },
            );

          const normalized =
            unwrapStudentList(
              response,
            );

          if (!cancelled) {
            setStudents(
              normalized.results,
            );

            setTotalCount(
              normalized.count,
            );

            setTotalPages(
              Math.max(
                normalized.totalPages ||
                  Math.ceil(
                    normalized.count /
                      pageSize,
                  ) ||
                  1,
                1,
              ),
            );
          }
        } catch (requestError) {
          if (!cancelled) {
            setStudents([]);
            setTotalCount(0);
            setTotalPages(1);

            setError(
              getApiErrorMessage(
                requestError,
                'Unable to load students.',
              ),
            );
          }
        } finally {
          if (!cancelled) {
            setLoading(false);
          }
        }
      },
      300,
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    schoolId,
    query,
    academicYear,
    classLevel,
    section,
    statusFilter,
    genderFilter,
    page,
    pageSize,
  ]);

  const paginationPages = useMemo(
    () =>
      Array.from(
        {
          length: totalPages,
        },
        (_, index) => index + 1,
      ).filter(
        (pageNumber) =>
          pageNumber === 1 ||
          pageNumber === totalPages ||
          Math.abs(
            pageNumber - page,
          ) <= 1,
      ),
    [totalPages, page],
  );

  const startNumber =
    totalCount === 0
      ? 0
      : (page - 1) * pageSize + 1;

  const endNumber = Math.min(
    page * pageSize,
    totalCount,
  );

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Students
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Search and filter students by
            academic year, class and section.
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            navigate('/students/add')
          }
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Student
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-6">
          <label className="relative block xl:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

            <input
              type="search"
              value={query}
              onChange={(event) => {
                setQuery(
                  event.target.value,
                );
                setPage(1);
              }}
              placeholder="Search name, admission or enrolment..."
              className="w-full rounded-lg border border-gray-300 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </label>

          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm">
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-blue-500">
              Working Academic Year
            </span>
            <span className="font-semibold text-blue-800">
              {activeAcademicYear?.year_name || 'No academic year selected'}
            </span>
          </div>

          <select
            value={classLevel}
            disabled={
              !academicYear ||
              loadingClasses
            }
            onChange={
              handleClassChange
            }
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              {loadingClasses
                ? 'Loading classes...'
                : academicYear
                  ? 'All classes'
                  : 'Select year first'}
            </option>

            {classLevels.map(
              (item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.class_name ||
                    item.name}
                </option>
              ),
            )}
          </select>

          <select
            value={section}
            disabled={
              !classLevel ||
              loadingSections
            }
            onChange={(event) => {
              setSection(
                event.target.value,
              );
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              {loadingSections
                ? 'Loading sections...'
                : classLevel
                  ? 'All sections'
                  : 'Select class first'}
            </option>

            {sections.map(
              (item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.section_name ||
                    item.name}
                </option>
              ),
            )}
          </select>

          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(
                event.target.value,
              );
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          >
            <option value="">
              All statuses
            </option>
            <option value="ACTIVE">
              Active
            </option>
            <option value="INACTIVE">
              Inactive
            </option>
            <option value="TRANSFERRED">
              Transferred
            </option>
            <option value="DROPPED">
              Dropped
            </option>
            <option value="GRADUATED">
              Graduated
            </option>
          </select>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select
            value={genderFilter}
            onChange={(event) => {
              setGenderFilter(
                event.target.value,
              );
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">
              All genders
            </option>
            <option value="MALE">
              Male
            </option>
            <option value="FEMALE">
              Female
            </option>
            <option value="OTHER">
              Other
            </option>
          </select>

          <button
            type="button"
            onClick={() => {
              setQuery('');
              setClassLevel('');
              setSection('');
              setStatusFilter('');
              setGenderFilter('');
              setSections([]);
              setPage(1);
            }}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Clear filters
          </button>
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px] text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-5 py-4 text-left">
                  Student
                </th>

                <th className="px-5 py-4 text-left">
                  Enrolment
                </th>

                <th className="px-5 py-4 text-left">
                  Academic Year
                </th>

                <th className="px-5 py-4 text-left">
                  Class
                </th>

                <th className="px-5 py-4 text-left">
                  Section
                </th>

                <th className="px-5 py-4 text-left">
                  Roll No.
                </th>

                <th className="px-5 py-4 text-left">
                  Status
                </th>

                <th className="px-5 py-4 text-center">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="py-14 text-center"
                  >
                    <Loader2 className="mx-auto h-7 w-7 animate-spin text-blue-600" />

                    <p className="mt-3 text-sm text-gray-500">
                      Loading students...
                    </p>
                  </td>
                </tr>
              ) : students.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="py-14 text-center"
                  >
                    <Users className="mx-auto h-10 w-10 text-gray-300" />

                    <p className="mt-3 font-semibold text-gray-700">
                      No students found
                    </p>

                    <p className="mt-1 text-sm text-gray-500">
                      Change the filters or add a student.
                    </p>
                  </td>
                </tr>
              ) : (
                students.map(
                  (student) => {
                    const academic =
                      student.current_academic ||
                      {};

                    const fullName =
                      student.full_name ||
                      [
                        student.first_name,
                        student.last_name,
                      ]
                        .filter(Boolean)
                        .join(' ');

                    return (
                      <tr
                        key={student.id}
                        className="border-t border-gray-100 hover:bg-gray-50"
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <StudentPhoto
                              photo={
                                student.photo
                              }
                              name={
                                fullName
                              }
                            />

                            <div>
                              <p className="font-semibold text-gray-900">
                                {fullName}
                              </p>

                              <p className="mt-0.5 text-xs text-gray-500">
                                {
                                  student.gender
                                }
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {student.enrolment_no ||
                            '—'}
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {academic.academic_year_name ||
                            '—'}
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {academic.class_name ||
                            '—'}
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {academic.section_name ||
                            '—'}
                        </td>

                        <td className="px-5 py-4 text-gray-700">
                          {academic.roll_no ||
                            '—'}
                        </td>

                        <td className="px-5 py-4">
                          <span
                            className={`rounded-full px-3 py-1 text-xs font-semibold ${
                              student.status ===
                              'ACTIVE'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {student.status}
                          </span>
                        </td>

                        <td className="px-5 py-4">
                          <div className="flex justify-center gap-2">
                            <button
                              type="button"
                              title="View student"
                              onClick={() =>
                                navigate(
                                  `/students/${student.id}`,
                                )
                              }
                              className="rounded-lg p-2 text-gray-400 hover:bg-blue-50 hover:text-blue-600"
                            >
                              <Eye className="h-4 w-4" />
                            </button>

                            <button
                              type="button"
                              title="Edit student"
                              onClick={() =>
                                navigate(
                                  `/students/${student.id}/edit`,
                                )
                              }
                              className="rounded-lg p-2 text-gray-400 hover:bg-orange-50 hover:text-orange-600"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  },
                )
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-4 border-t border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-sm text-gray-500">
              Showing {startNumber} to{' '}
              {endNumber} of{' '}
              {totalCount} students
            </p>

            <select
              value={pageSize}
              onChange={(event) => {
                setPageSize(
                  Number(
                    event.target.value,
                  ),
                );
                setPage(1);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value={10}>
                10 per page
              </option>
              <option value={25}>
                25 per page
              </option>
              <option value={50}>
                50 per page
              </option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() =>
                setPage((current) =>
                  Math.max(
                    current - 1,
                    1,
                  ),
                )
              }
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>

            {paginationPages.map(
              (pageNumber, index) => {
                const previousPage =
                  paginationPages[
                    index - 1
                  ];

                return (
                  <React.Fragment
                    key={pageNumber}
                  >
                    {previousPage &&
                      pageNumber -
                        previousPage >
                        1 && (
                        <span className="px-1 text-gray-400">
                          …
                        </span>
                      )}

                    <button
                      type="button"
                      onClick={() =>
                        setPage(
                          pageNumber,
                        )
                      }
                      className={`h-9 w-9 rounded-lg text-sm ${
                        page ===
                        pageNumber
                          ? 'bg-blue-600 text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNumber}
                    </button>
                  </React.Fragment>
                );
              },
            )}

            <button
              type="button"
              disabled={
                page >= totalPages
              }
              onClick={() =>
                setPage((current) =>
                  Math.min(
                    current + 1,
                    totalPages,
                  ),
                )
              }
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StudentListPage;
